import type { ChangeEvent } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { X } from 'react-feather';
import { Search } from '@/components/icons/Search';
import { ProjectClipsFiltersContext, SetProjectClipsFiltersContext } from './context';
import styles from './style/KeywordSearch.module.css';

export const KeywordSearch = () => {

  const filters = useContext(ProjectClipsFiltersContext);
  const setFilters = useContext(SetProjectClipsFiltersContext);
  const [value, setValue] = useState(filters.keyword);

  const updateFilters = useCallback((keyword: string) => {
    setFilters({
      keyword,
      speakerRole: keyword ? filters.speakerRole : 'all',
    });
  }, [filters.speakerRole, setFilters]);

  const clearFilter = useCallback(() => {
    setValue('');
    setFilters({
      keyword: '',
    });
  }, [setFilters]);

  const debouncedUpdateFilters = useDebounceCallback(updateFilters, 300);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    debouncedUpdateFilters(e.target.value);
  }, [debouncedUpdateFilters]);

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <div className={styles.left}>
          <Search
            className={styles.icon}
            size={22} />
          <input
            className={styles.input}
            autoComplete="off"
            name="highlights-search"
            placeholder="Search transcript text"
            type="text"
            onChange={handleChange}
            value={value} />
        </div>
        {value && (
          <X
            className={styles.x}
            onClick={clearFilter}
            size={22} />
        )}
      </div>
    </div>
  );
};

const useDebounceCallback = <CallbackReturnType, CallbackArgs extends unknown[] = []>(
  callback: (...args: CallbackArgs) => CallbackReturnType,
  wait = 100,
): ((...args: CallbackArgs) => void) => {
  const timeout = useRef<NodeJS.Timeout>(null);

  useEffect(() => () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  return useCallback((...args: CallbackArgs) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    timeout.current = setTimeout(() => {
      timeout.current = null;
      callback(...args);
    }, wait);
  }, [callback, wait]);
};